import React, { useMemo, useRef } from "react"
import styled from "styled-components"

import useTracking from "hooks/useTracking"
import { useComponentizedPage } from "store/contexts/componentized-page"

import Icon from "icons"
import CTAButton from "components/button/cta"
import BuyButton from "components/button/buy"

const ContentfulButton = ({ component }) => {
  const {
    id,
    action,
    buttonColor,
    buttonStyle,
    size,
    targetUrl,
    targetNavigationAnchor,
    text,
    goToCart,
    quantity,
    targetClass,
    targetBundle,
    displayPriceOnButton,
    disclaimerText,
    discountText,
    tracking,
    trackingOverride,
    trackingId,
    mailToAddress,
    isWebinarPromotion,
    showInstallmentPaymentLanguage,
    shopifyProductVariant,
  } = component

  const { track, trackRender, trackView, trackClick } = useTracking()
  const ref = useRef(null)

  if (tracking && tracking.includes("Render")) {
    if (trackingOverride && "Render" in trackingOverride) {
      trackRender(ref, trackingOverride.Render.trackingId, trackingOverride.Render.props, (props) =>
        track(trackingOverride.Render.event, props),
      )
    } else {
      trackRender(ref, trackingId)
    }
  }

  if (tracking && tracking.includes("View")) {
    if (trackingOverride && "View" in trackingOverride) {
      trackView(ref, trackingOverride.View.trackingId, trackingOverride.View.props, (props) =>
        track(trackingOverride.View.event, props),
      )
    } else {
      trackView(ref, trackingId)
    }
  }

  if (tracking && tracking.includes("Click")) {
    if (trackingOverride && "Click" in trackingOverride) {
      trackClick(ref, trackingOverride.Click.trackingId, trackingOverride.Click.props, (props) =>
        track(trackingOverride.Click.event, props),
      )
    } else if (isWebinarPromotion) {
      trackClick(ref, trackingId, { promotionId: trackingId, position: "header" }, (props) =>
        track("Promotion Clicked", props),
      )
    } else {
      trackClick(ref, trackingId)
    }
  }

  const {
    class: pageClass,
    shopifyProductVariant: pageShopifyProductVariant,
    classesOnPage,
    classBundles,
  } = useComponentizedPage()

  const Button = disclaimerText || discountText ? BuyButton : CTAButton
  const discountClean =
    discountText && discountText.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")

  const productCartData = useMemo(() => {
    const variant = shopifyProductVariant || pageShopifyProductVariant

    if (variant && ["add to cart", "add subscription to cart"].includes(action.toLowerCase())) {
      const sellingPlan = variant.skioSellingPlanGroupResource?.sellingPlan
        ? variant.skioSellingPlanGroupResource.sellingPlan
        : null

      const skioSellingPlan =
        action.toLowerCase() === "add subscription to cart" ? sellingPlan : null

      return {
        productId: variant.variantId,
        productPrice: variant.price,
        productTitle: variant.product.title,
        quantity,
        redirect: !!goToCart,
        variant,
        sellingPlanId: skioSellingPlan?.sellingPlanResourceId,
      }
    }
  }, [shopifyProductVariant, pageShopifyProductVariant, action, quantity, goToCart])

  const extraProps = {
    title: text,
  }

  if (action === "None") {
    extraProps.actionNone = true
  }

  if (action === "Link to URL") {
    extraProps.link = targetUrl
    extraProps.ariaLabel = `Open link to ${targetUrl}`
  }

  if (action === "Link to Anchor") {
    extraProps.anchor = `#${targetNavigationAnchor?.anchorId}`
    extraProps.title = targetNavigationAnchor?.title
  }

  if (action === "Add to Cart" && productCartData) {
    extraProps.addProductToCart = productCartData
    extraProps.price = productCartData.productPrice
  }

  if (["Add to Cart", "Link to Anchor"].includes(action) && (targetClass || pageClass)) {
    const id = (targetClass || pageClass).classId
    const cls = classesOnPage.find((c) => c.classId === id)
    if (!cls) return null // bad button settings

    if (action === "Add to Cart") {
      extraProps.addClassToCart = {
        classId: cls.classId,
        classTitle: cls.title,
        classPrice: cls.salePrice || cls.price,
        redirect: !!goToCart,
        paymentType: "one-time",
        isBundle: false,
      }
    }
    extraProps.price = cls.salePrice || cls.price
  }

  if (action === "Add to Cart" && targetBundle) {
    // targetBundle is the bundle id (UUID) from CMS. We need to find the bundle in the classBundles array
    // classBundles from graphql has id like class-bundle-UUID
    const classBundle = classBundles.find((bundle) => bundle.id.match(targetBundle))
    if (!classBundle) return null // bad button settings

    const price =
      classBundle.classes.reduce((agg, c) => agg + c.price, 0) * (1 - classBundle.savingsPercentage)
    extraProps.addClassToCart = {
      classId: targetBundle,
      classTitle: classBundle.title,
      classPrice: price,
      redirect: !!goToCart,
      paymentType: "one-time",
      isBundle: true,
    }
    extraProps.price = price
  }

  if (action.toLowerCase() === "add subscription to cart" && productCartData) {
    const regularPrice = parseFloat(productCartData.productPrice)

    const discount = parseFloat(productCartData.skioSellingPlan?.fixedPrice)
    if (discount && !Number.isNaN(discount)) {
      const discountedPrice = regularPrice - regularPrice * (discount / 100)
      extraProps.price = discountedPrice
    }
    extraProps.addSubscriptionToCart = productCartData
  }

  if (mailToAddress && action === "Send Email") {
    extraProps.mailToAddress = mailToAddress
  }

  const renderText = () => {
    if (text.startsWith("+")) {
      return (
        <>
          <PlusIcon id="plus-button" width={20} height={20} />
          {text.replace("+", "").trim()}
        </>
      )
    }

    return text
  }

  return (
    <>
      <Button
        dataId={id}
        elRef={ref}
        primary={buttonColor === "Primary CTA"}
        size={size}
        buttonStyle={buttonStyle}
        displayPriceOnButton={displayPriceOnButton}
        arrowRight={!!displayPriceOnButton}
        disclaimerText={disclaimerText && disclaimerText.childMarkdownRemark.html}
        discountText={discountClean}
        aria-label={extraProps.ariaLabel}
        showInstallmentPaymentLanguage={showInstallmentPaymentLanguage}
        {...extraProps}
      >
        {renderText()}
      </Button>{" "}
    </>
  )
}

const PlusIcon = styled(Icon)`
  margin-right: 0.8rem;
  color: ${({ theme }) =>
    theme.component.isDarkBackground ? theme.colors.text.primary : theme.colors.text.alternate};
`

export default ContentfulButton
